/**
 * ViewModel contract (RFC §3a/§4) + the ViewModelProvider seam.
 *
 * Two distinct seams, not one (RFC §4): the ViewModelProvider is a model→VM
 * TRANSFORMER REGISTRY (ported from domain-studio provider.ts — create/update/
 * memoize) answering "which VM transform for this element kind"; the IoC
 * container (context.ts) answers "where do its constructor dependencies come
 * from". Do not conflate them.
 *
 * Port notes (deliberate trims from domain-studio provider.ts):
 * - Registry keys collapsed from (domainName, typeName) pairs to ONE `kind`
 *   string — the RFC frames the provider as keyed by element kind (§4).
 * - `composeProviders()` dropped: unused by the RFC; compose with plain
 *   functions instead.
 * - The global `ViewModelProviders` singleton dropped: a mutable module-level
 *   registry conflicts with the IoC design — apps create a registry at the
 *   composition root and provide it via a context token.
 */

import { signal, computedFrom, type ReadonlySignal, type Signal, type Computed } from "./signal";

/** Teardown returned by `ViewModel.attach()` and other wire-ups. */
export type Dispose = () => void;

/**
 * The element-facing ViewModel contract (RFC §3a).
 * Plain, constructor-injected classes/objects — testable without DOM.
 */
export interface ViewModel {
  /**
   * Signals whose changes re-render the host. Explicit and enumerable —
   * the select()-slice idea, typed. KitElement subscribes via `effect()`
   * on connect and disposes on disconnect.
   */
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>;
  /** Wire-up: subscribe to stores/push; return teardown. Called on connect. */
  attach?(): Dispose;
}

/**
 * Model→ViewModel transformer (ported from domain-studio).
 *
 * @example
 * ```ts
 * const orderProvider: ViewModelProvider<Order, OrderVm> = {
 *   create: (order) => ({ ...order, totalFormatted: formatCurrency(total(order)) }),
 * };
 * ```
 */
export interface ViewModelProvider<TModel, TViewModel> {
  /** Create a view model from a model. Called when the model is first loaded. */
  create(model: TModel): TViewModel;
  /** Update a view model when the model changes. Falls back to `create()`. */
  update?(model: TModel, current: TViewModel): TViewModel;
  /** Cleanup resources when the view model is no longer needed. */
  dispose?(): void;
}

/** Registry of ViewModel providers keyed by element kind. */
export class ViewModelProviderRegistry {
  #providers = new Map<string, ViewModelProvider<unknown, unknown>>();

  /** Register a provider for an element kind (e.g. "career-opportunity-card"). */
  register<TModel, TViewModel>(
    kind: string,
    provider: ViewModelProvider<TModel, TViewModel>
  ): void {
    this.#providers.set(kind, provider as ViewModelProvider<unknown, unknown>);
  }

  get<TModel, TViewModel>(
    kind: string
  ): ViewModelProvider<TModel, TViewModel> | undefined {
    return this.#providers.get(kind) as
      | ViewModelProvider<TModel, TViewModel>
      | undefined;
  }

  has(kind: string): boolean {
    return this.#providers.has(kind);
  }

  unregister(kind: string): boolean {
    return this.#providers.delete(kind);
  }

  /** All registered kinds. */
  list(): string[] {
    return [...this.#providers.keys()];
  }

  clear(): void {
    this.#providers.clear();
  }
}

/** Reactive model→VM binding: the VM recomputes when the model signal changes. */
export interface ReactiveViewModel<TModel, TViewModel> {
  /** Current model signal. */
  readonly model: Signal<TModel>;
  /** Computed view model. */
  readonly viewModel: Computed<TViewModel>;
  /** Update the model. */
  setModel(model: TModel): void;
  /** Dispose resources. */
  dispose(): void;
}

/**
 * Create a reactive view model binding (ported from domain-studio).
 * Useful for URN rebinding: swap the model, the derived VM follows.
 */
export function createReactiveViewModel<TModel, TViewModel>(
  initialModel: TModel,
  provider: ViewModelProvider<TModel, TViewModel>
): ReactiveViewModel<TModel, TViewModel> {
  const modelSignal = signal(initialModel);

  // Track the previous view model for update().
  let currentViewModel = provider.create(initialModel);

  const viewModel = computedFrom([modelSignal], ([model]) => {
    currentViewModel = provider.update
      ? provider.update(model, currentViewModel)
      : provider.create(model);
    return currentViewModel;
  });

  return {
    model: modelSignal,
    viewModel,
    setModel(model: TModel) {
      modelSignal.set(model);
    },
    dispose() {
      provider.dispose?.();
    },
  };
}

/** Create a simple provider from a transform function. */
export function createProvider<TModel, TViewModel>(
  transform: (model: TModel) => TViewModel
): ViewModelProvider<TModel, TViewModel> {
  return { create: transform };
}

/**
 * Create a provider with a memoized transform: recomputes only when the model
 * reference changes, or per a custom `shouldUpdate` predicate.
 */
export function createMemoizedProvider<TModel, TViewModel>(
  transform: (model: TModel) => TViewModel,
  shouldUpdate?: (prev: TModel, next: TModel) => boolean
): ViewModelProvider<TModel, TViewModel> {
  let prevModel: TModel | undefined;
  let cachedViewModel: TViewModel | undefined;

  return {
    create(model: TModel): TViewModel {
      cachedViewModel = transform(model);
      prevModel = model;
      return cachedViewModel;
    },
    update(model: TModel, _current: TViewModel): TViewModel {
      const needsUpdate = shouldUpdate
        ? shouldUpdate(prevModel as TModel, model)
        : prevModel !== model;

      if (!needsUpdate && cachedViewModel !== undefined) {
        return cachedViewModel;
      }

      cachedViewModel = transform(model);
      prevModel = model;
      return cachedViewModel;
    },
  };
}
